import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { hasRole, hasAnyRole, isSuperAdmin } from "../lib/access-control";
import { isModuleEnabled } from "../lib/modules";

export default function RoleMiddleware({ role, roles, module, requiresFeature, requiresPlatformAdmin, fallbackPath = "/", children }) {
  const user = useSelector((s) => s.users.user);
  const settings = useSelector((s) => s.settings.data);
  const features = settings?.features;

  // Checked ahead of the super-admin bypass below — a clinic-level feature
  // toggle means "off for this clinic, full stop," not "hidden from
  // everyone except whoever has the most access."
  if (requiresFeature && features?.[requiresFeature] === false) {
    return <Navigate to={fallbackPath} replace />;
  }
  // Same for a sidebar module Platform Admin has switched off for this clinic.
  if (!isModuleEnabled(settings, module)) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Also checked ahead of the super-admin bypass — platform_admin is an
  // Aivexa-internal account flag, not a role, so a client's own super-admin
  // must never reach this just by being a super-admin within their own clinic.
  if (requiresPlatformAdmin && !user?.platform_admin) {
    return <Navigate to={fallbackPath} replace />;
  }

  // A platform_admin account is Aivexa-internal and exclusive to the
  // Platform Admin section — it never sees this clinic's own client-facing
  // pages (Dashboard, Patients, Settings, etc.), even if it also happens to
  // hold a clinic role.
  if (!requiresPlatformAdmin && user?.platform_admin) {
    return <Navigate to="/platform-admin" replace />;
  }

  if (isSuperAdmin(user)) return children;

  const allowed = role ? hasRole(user, role) : roles ? hasAnyRole(user, roles) : true;

  if (!allowed) return <Navigate to={fallbackPath} replace />;
  return children;
}
